import * as Schema from "effect/Schema";
import * as Rpc from "effect/unstable/rpc/Rpc";
import * as RpcGroup from "effect/unstable/rpc/RpcGroup";
import { ExternalLauncherError, OpenArtifactInput } from "./editor.js";
import { ServerTokenRefreshError, ServerTokenRefreshInput, ServerTokenRefreshResult, EnvironmentAuthorizationError, } from "./authorization.js";
import { KeybindingsConfigError } from "./keybindings.js";
import { OrganizationQuotas } from "./quotas.js";
import { ClientOrchestrationCommand, ORCHESTRATION_WS_METHODS, OrchestrationDispatchCommandError, OrchestrationGetSnapshotError, OrchestrationReplayEventsError, OrchestrationReplayEventsInput, OrchestrationRpcSchemas, } from "./orchestration.js";
import { ProjectDeleteDocumentError, ProjectCopyOutputError, ProjectCopyOutputToMaterialsInput, ProjectCopyOutputToMaterialsResult, ProjectDeleteDocumentInput, ProjectDeleteDocumentResult, ProjectDeleteOutputError, ProjectDeleteOutputFileInput, ProjectDeleteOutputFileResult, ProjectDocumentsStatusError, ProjectDocumentsStatusInput, ProjectDocumentsStatusResult, ProjectOutputStatusError, ProjectOutputStatusInput, ProjectOutputStatusResult, ProjectSearchEntriesError, ProjectSearchEntriesInput, ProjectSearchEntriesResult, } from "./project.js";
import { ServerConfigStreamEvent, ServerConfig, ServerLifecycleStreamEvent, ServerRemoveKeybindingInput, ServerRemoveKeybindingResult, ServerTraceDiagnosticsResult, ServerProcessDiagnosticsResult, ServerProcessResourceHistoryInput, ServerProcessResourceHistoryResult, ServerSignalProcessInput, ServerSignalProcessResult, ServerUpsertKeybindingInput, ServerUpsertKeybindingResult, } from "./server.js";
export const WS_METHODS = {
    // Project registry methods
    projectsList: "projects.list",
    projectsAdd: "projects.add",
    projectsRemove: "projects.remove",
    projectsSearchEntries: "projects.searchEntries",
    projectsDocumentsStatus: "projects.documentsStatus",
    projectsDeleteDocument: "projects.deleteDocument",
    projectsOutputStatus: "projects.outputStatus",
    projectsCopyOutputToMaterials: "projects.copyOutputToMaterials",
    projectsDeleteOutputFile: "projects.deleteOutputFile",
    // Shell methods
    shellOpenArtifact: "shell.openArtifact",
    // Server meta
    serverGetConfig: "server.getConfig",
    serverRefreshQuotas: "server.refreshQuotas",
    serverUpsertKeybinding: "server.upsertKeybinding",
    serverRemoveKeybinding: "server.removeKeybinding",
    serverGetTraceDiagnostics: "server.getTraceDiagnostics",
    serverGetProcessDiagnostics: "server.getProcessDiagnostics",
    serverGetProcessResourceHistory: "server.getProcessResourceHistory",
    serverSignalProcess: "server.signalProcess",
    // Streaming subscriptions
    subscribeServerConfig: "subscribeServerConfig",
    subscribeServerLifecycle: "subscribeServerLifecycle",
    serverRefreshAuthToken: "server.refreshAuthToken",
};
export const WsServerUpsertKeybindingRpc = Rpc.make(WS_METHODS.serverUpsertKeybinding, {
    payload: ServerUpsertKeybindingInput,
    success: ServerUpsertKeybindingResult,
    error: Schema.Union([KeybindingsConfigError, EnvironmentAuthorizationError]),
});
export const WsServerRemoveKeybindingRpc = Rpc.make(WS_METHODS.serverRemoveKeybinding, {
    payload: ServerRemoveKeybindingInput,
    success: ServerRemoveKeybindingResult,
    error: Schema.Union([KeybindingsConfigError, EnvironmentAuthorizationError]),
});
export const WsServerGetConfigRpc = Rpc.make(WS_METHODS.serverGetConfig, {
    payload: Schema.Struct({}),
    success: ServerConfig,
    error: Schema.Union([KeybindingsConfigError, EnvironmentAuthorizationError]),
});
export const WsServerRefreshQuotasRpc = Rpc.make(WS_METHODS.serverRefreshQuotas, {
    payload: Schema.Struct({}),
    success: Schema.NullOr(OrganizationQuotas),
    error: EnvironmentAuthorizationError,
});
export const WsServerGetTraceDiagnosticsRpc = Rpc.make(WS_METHODS.serverGetTraceDiagnostics, {
    payload: Schema.Struct({}),
    success: ServerTraceDiagnosticsResult,
    error: EnvironmentAuthorizationError,
});
export const WsServerGetProcessDiagnosticsRpc = Rpc.make(WS_METHODS.serverGetProcessDiagnostics, {
    payload: Schema.Struct({}),
    success: ServerProcessDiagnosticsResult,
    error: EnvironmentAuthorizationError,
});
export const WsServerGetProcessResourceHistoryRpc = Rpc.make(WS_METHODS.serverGetProcessResourceHistory, {
    payload: ServerProcessResourceHistoryInput,
    success: ServerProcessResourceHistoryResult,
    error: EnvironmentAuthorizationError,
});
export const WsServerSignalProcessRpc = Rpc.make(WS_METHODS.serverSignalProcess, {
    payload: ServerSignalProcessInput,
    success: ServerSignalProcessResult,
    error: EnvironmentAuthorizationError,
});
export const WsProjectsSearchEntriesRpc = Rpc.make(WS_METHODS.projectsSearchEntries, {
    payload: ProjectSearchEntriesInput,
    success: ProjectSearchEntriesResult,
    error: Schema.Union([ProjectSearchEntriesError, EnvironmentAuthorizationError]),
});
export const WsProjectsDocumentsStatusRpc = Rpc.make(WS_METHODS.projectsDocumentsStatus, {
    payload: ProjectDocumentsStatusInput,
    success: ProjectDocumentsStatusResult,
    error: Schema.Union([ProjectDocumentsStatusError, EnvironmentAuthorizationError]),
});
export const WsProjectsDeleteDocumentRpc = Rpc.make(WS_METHODS.projectsDeleteDocument, {
    payload: ProjectDeleteDocumentInput,
    success: ProjectDeleteDocumentResult,
    error: Schema.Union([ProjectDeleteDocumentError, EnvironmentAuthorizationError]),
});
export const WsProjectsOutputStatusRpc = Rpc.make(WS_METHODS.projectsOutputStatus, {
    payload: ProjectOutputStatusInput,
    success: ProjectOutputStatusResult,
    error: Schema.Union([ProjectOutputStatusError, EnvironmentAuthorizationError]),
});
export const WsProjectsCopyOutputToMaterialsRpc = Rpc.make(WS_METHODS.projectsCopyOutputToMaterials, {
    payload: ProjectCopyOutputToMaterialsInput,
    success: ProjectCopyOutputToMaterialsResult,
    error: Schema.Union([ProjectCopyOutputError, EnvironmentAuthorizationError]),
});
export const WsProjectsDeleteOutputFileRpc = Rpc.make(WS_METHODS.projectsDeleteOutputFile, {
    payload: ProjectDeleteOutputFileInput,
    success: ProjectDeleteOutputFileResult,
    error: Schema.Union([ProjectDeleteOutputError, EnvironmentAuthorizationError]),
});
export const WsShellOpenArtifactRpc = Rpc.make(WS_METHODS.shellOpenArtifact, {
    payload: OpenArtifactInput,
    error: Schema.Union([ExternalLauncherError, EnvironmentAuthorizationError]),
});
export const WsOrchestrationDispatchCommandRpc = Rpc.make(ORCHESTRATION_WS_METHODS.dispatchCommand, {
    payload: ClientOrchestrationCommand,
    success: OrchestrationRpcSchemas.dispatchCommand.output,
    error: Schema.Union([OrchestrationDispatchCommandError, EnvironmentAuthorizationError]),
});
export const WsOrchestrationReplayEventsRpc = Rpc.make(ORCHESTRATION_WS_METHODS.replayEvents, {
    payload: OrchestrationReplayEventsInput,
    success: OrchestrationRpcSchemas.replayEvents.output,
    error: Schema.Union([OrchestrationReplayEventsError, EnvironmentAuthorizationError]),
});
export const WsOrchestrationGetArchivedShellSnapshotRpc = Rpc.make(ORCHESTRATION_WS_METHODS.getArchivedShellSnapshot, {
    payload: OrchestrationRpcSchemas.getArchivedShellSnapshot.input,
    success: OrchestrationRpcSchemas.getArchivedShellSnapshot.output,
    error: Schema.Union([OrchestrationGetSnapshotError, EnvironmentAuthorizationError]),
});
export const WsOrchestrationSubscribeShellRpc = Rpc.make(ORCHESTRATION_WS_METHODS.subscribeShell, {
    payload: OrchestrationRpcSchemas.subscribeShell.input,
    success: OrchestrationRpcSchemas.subscribeShell.output,
    error: Schema.Union([OrchestrationGetSnapshotError, EnvironmentAuthorizationError]),
    stream: true,
});
export const WsOrchestrationSubscribeThreadRpc = Rpc.make(ORCHESTRATION_WS_METHODS.subscribeThread, {
    payload: OrchestrationRpcSchemas.subscribeThread.input,
    success: OrchestrationRpcSchemas.subscribeThread.output,
    error: Schema.Union([OrchestrationGetSnapshotError, EnvironmentAuthorizationError]),
    stream: true,
});
export const WsOrchestrationFetchOlderThreadMessagesRpc = Rpc.make(ORCHESTRATION_WS_METHODS.fetchOlderThreadMessages, {
    payload: OrchestrationRpcSchemas.fetchOlderThreadMessages.input,
    success: OrchestrationRpcSchemas.fetchOlderThreadMessages.output,
    error: Schema.Union([OrchestrationGetSnapshotError, EnvironmentAuthorizationError]),
});
export const WsOrchestrationFetchOlderThreadActivitiesRpc = Rpc.make(ORCHESTRATION_WS_METHODS.fetchOlderThreadActivities, {
    payload: OrchestrationRpcSchemas.fetchOlderThreadActivities.input,
    success: OrchestrationRpcSchemas.fetchOlderThreadActivities.output,
    error: Schema.Union([OrchestrationGetSnapshotError, EnvironmentAuthorizationError]),
});
export const WsOrchestrationExportThreadDebugLogRpc = Rpc.make(ORCHESTRATION_WS_METHODS.exportThreadDebugLog, {
    payload: OrchestrationRpcSchemas.exportThreadDebugLog.input,
    success: OrchestrationRpcSchemas.exportThreadDebugLog.output,
    error: Schema.Union([OrchestrationGetSnapshotError, EnvironmentAuthorizationError]),
});
export const WsSubscribeServerConfigRpc = Rpc.make(WS_METHODS.subscribeServerConfig, {
    payload: Schema.Struct({}),
    success: ServerConfigStreamEvent,
    error: Schema.Union([KeybindingsConfigError, EnvironmentAuthorizationError]),
    stream: true,
});
export const WsSubscribeServerLifecycleRpc = Rpc.make(WS_METHODS.subscribeServerLifecycle, {
    payload: Schema.Struct({}),
    success: ServerLifecycleStreamEvent,
    error: EnvironmentAuthorizationError,
    stream: true,
});
/**
 * The renderer's ~12-minute token refresh, pushed over the **already-open**
 * connection.
 *
 * It is an RPC rather than a reconnect because reconnecting would tear down
 * every live subscription on a 12-minute cycle.
 */
export const WsServerRefreshAuthTokenRpc = Rpc.make(WS_METHODS.serverRefreshAuthToken, {
    payload: ServerTokenRefreshInput,
    success: ServerTokenRefreshResult,
    error: Schema.Union([ServerTokenRefreshError, EnvironmentAuthorizationError]),
});
export const WsRpcGroup = RpcGroup.make(WsServerGetConfigRpc, WsServerRefreshQuotasRpc, WsServerUpsertKeybindingRpc, WsServerRemoveKeybindingRpc, WsServerGetTraceDiagnosticsRpc, WsServerGetProcessDiagnosticsRpc, WsServerGetProcessResourceHistoryRpc, WsServerSignalProcessRpc, WsProjectsSearchEntriesRpc, WsProjectsDocumentsStatusRpc, WsProjectsDeleteDocumentRpc, WsProjectsOutputStatusRpc, WsProjectsCopyOutputToMaterialsRpc, WsProjectsDeleteOutputFileRpc, WsShellOpenArtifactRpc, WsSubscribeServerConfigRpc, WsSubscribeServerLifecycleRpc, WsServerRefreshAuthTokenRpc, WsOrchestrationDispatchCommandRpc, WsOrchestrationReplayEventsRpc, WsOrchestrationGetArchivedShellSnapshotRpc, WsOrchestrationSubscribeShellRpc, WsOrchestrationSubscribeThreadRpc, WsOrchestrationFetchOlderThreadMessagesRpc, WsOrchestrationFetchOlderThreadActivitiesRpc, WsOrchestrationExportThreadDebugLogRpc);
